import React, { useState } from 'react'
import DraggableModal from '../../../generic/DraggableModal'
import { backendPostWithoutRefresh } from '../../../../helpers/callBackend'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import StyledButton from '../../../generic/StyledButton'
import Stack from '@mui/material/Stack'
import StyledSelect from '../../../generic/StyledSelect'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { errorMessage } from '../../../../helpers/errorMessageFunc'
import checkFlag from '../../../../helpers/featureFlags'
import SelectBankAccountTag from '../../customTags/selectBankAccountTag'
import Typograhy from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'

const TeamInviteModal = ({
  currentUserCompanyPermissions,
  setUserCompanyPermissions,
  modalVisible,
  setModalVisible,
}) => {
  const [users, setUsers] = useState([
    { id: 0, firstName: '', lastName: '', email: '', role: 'member', accessTags: [] },
  ])
  const roles = [
    {
      display_text: 'Member',
      value: 'member',
    },
    {
      display_text: 'Admin',
      value: 'admin',
    },
  ]

  if (checkFlag('dashboardEditor')) {
    roles.push({
      display_text: 'Dashboard Editor',
      value: 'editor',
    })
  }

  const rolePermissionsMap = {
    member: {
      is_admin: false,
      dashboard_editor: false,
    },
    admin: {
      is_admin: true,
      dashboard_editor: true,
    },
    editor: {
      is_admin: false,
      dashboard_editor: true,
    },
  }

  const validateInput = (user) => {
    try {
      if (!user.email) {
        throw new Error('Email required')
      }

      if (!/\S+@\S+\.\S+/.test(user.email)) {
        throw new Error(`${user.email} is not a valid email`)
      }

      if (user.lastName && !user.firstName) {
        throw new Error(`Please provide a first name for ${user.email}`)
      }
    } catch (error) {
      error.message = `Missing Information: ${error.message}`
      throw error
    }
  }

  const handleSubmit = async () => {
    // validate input data
    try {
      users.forEach((user) => validateInput(user))
    } catch (error) {
      errorMessage(error.message)
      return
    }
    const colleageEmails = users.map((user) => user.email)
    const invitations = users.map((user) => {
      const rolePermissions = rolePermissionsMap[user.role]
      return {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
        account_access_tags: user.accessTags,
        ...rolePermissions,
      }
    })
    try {
      const inviteResp = await backendPostWithoutRefresh('/api/landlord/invite/', {
        colleague_emails: colleageEmails,
        invitations: invitations,
      })
      let newUcsObjects = [...currentUserCompanyPermissions, ...inviteResp.data?.data]
      setUserCompanyPermissions(newUcsObjects)
      setUsers([{ id: 0, firstName: '', lastName: '', email: '', role: 'member', accessTags: [] }])
      setModalVisible(false)
    } catch (error) {
      errorMessage(error.message)
    }
  }

  const handleInputChange = (id, field, value) => {
    const updatedUsers = users.map((user) => {
      if (user.id === id) {
        const updatedUser = { ...user, [field]: value }

        return updatedUser
      }
      return user
    })

    setUsers(updatedUsers)
  }

  const handleAddRow = () => {
    const newId = users.length > 0 ? Math.max(...users.map((user) => user.id)) + 1 : 1
    setUsers([...users, { id: newId, firstName: '', lastName: '', email: '', role: 'member' }])
  }

  const handleRemoveRow = (id) => {
    if (users.length == 1) {
      // clear users info but don't remove last row
      setUsers([{ id: 0, firstName: '', lastName: '', email: '', role: 'member' }])
    } else {
      // otherwise just remove the user data from the form
      setUsers(users.filter((user) => user.id !== id))
    }
  }

  const textFieldStyle = {
    fullWidth: true,
    size: 'small',
    InputProps: {
      style: {
        borderRadius: 0,
      },
    },
  }

  const columnWidths = {
    firstName: '15%',
    lastName: '15%',
    email: '15%',
    role: '15%',
    access: '20%',
    dashboardEditor: '15%',
    remove: '10%',
  }

  return (
    <DraggableModal
      width='80%'
      maxWidth='10000px' // allow table to fill wider modal
      modalVisible={modalVisible}
      handleCancelModal={setModalVisible}
      modalTitle='Invite Teammates'
      content={
        <TableContainer sx={{ mb: 2, width: '100%' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }} size='small'>
            <TableHead>
              <TableRow>
                <TableCell width={columnWidths.firstName}>
                  <Typograhy>First Name</Typograhy>
                </TableCell>
                <TableCell width={columnWidths.lastName}>
                  <Typograhy>Last Name</Typograhy>
                </TableCell>
                <TableCell width={columnWidths.email}>
                  <Typograhy>Email</Typograhy>
                </TableCell>
                <TableCell width={columnWidths.role}>
                  <Typograhy>Role</Typograhy>
                </TableCell>
                <TableCell width={columnWidths.access}>
                  <Tooltip
                    placement='top'
                    title={
                      <Typograhy>Select a tag to limit access. Otherwise the user will have full access.</Typograhy>
                    }
                  >
                    <Typograhy>Access Tags</Typograhy>
                  </Tooltip>
                </TableCell>
                <TableCell width={columnWidths.remove}>
                  <Typograhy>Remove</Typograhy>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} sx={{ width: '100%' }}>
                  <TableCell>
                    <TextField
                      placeholder='First Name'
                      value={user.firstName}
                      onChange={(e) => handleInputChange(user.id, 'firstName', e.target.value)}
                      error={user.lastName && !user.firstName}
                      {...textFieldStyle}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder='Last Name'
                      value={user.lastName}
                      onChange={(e) => handleInputChange(user.id, 'lastName', e.target.value)}
                      {...textFieldStyle}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder='example@email.com'
                      value={user.email}
                      onChange={(e) => handleInputChange(user.id, 'email', e.target.value)}
                      type='email'
                      error={!user.email}
                      {...textFieldStyle}
                    />
                  </TableCell>
                  <TableCell>
                    <StyledSelect
                      onChange={(event) => handleInputChange(user.id, 'role', event.target.value)}
                      value={user.role ? user.role : 'member'}
                      items={roles}
                    />
                  </TableCell>
                  <TableCell>
                    <SelectBankAccountTag
                      includeUncategorized={false}
                      multiSelect={true}
                      onChange={(event) => handleInputChange(user.id, 'accessTags', event)}
                      placeholder='Select access tags.'
                      loadInComponent={true}
                      size='large'
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleRemoveRow(user.id)} aria-label='delete'>
                      <RemoveCircleOutlineIcon fontSize='large' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      }
      actions={
        <Stack sx={{ width: '97%' }} spacing={2}>
          <StyledButton label='Invite User' fontSize={14} customTheme='outlined' onClick={handleAddRow} />
          <StyledButton label='Submit' fontSize={14} onClick={handleSubmit} />
        </Stack>
      }
    />
  )
}

export default TeamInviteModal
